import { Avatar, Badge, Dropdown, Menu } from '@arco-design/web-react'
import { IconSettings, IconPoweroff, IconUser } from '@arco-design/web-react/icon'
import { Bell, BookOpen, Menu as MenuIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import LanguageSwitcher from '@/components/Common/LanguageSwitcher'
import ConsoleBreadcrumb from '@/components/Layout/ConsoleBreadcrumb'
import ConsoleIconButton from '@/components/Layout/ConsoleIconButton'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { getUserInitial } from '@/lib/userDisplayName'
import type { User } from '@/types'

interface ConsoleTopbarProps {
  user: User | null
  isRoot: boolean
  displayName: string
  onOpenMobileNav: () => void
  onLogout: () => void
}

export default function ConsoleTopbar({
  user,
  isRoot,
  displayName,
  onOpenMobileNav,
  onLogout,
}: ConsoleTopbarProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isMobile } = useBreakpoint()

  const userDropdownItems = [
    {
      key: 'profile',
      icon: <IconUser />,
      label: t('layout.profile'),
      onClick: () => navigate('/console/personal'),
    },
    ...(isMobile
      ? [
          {
            key: 'portal',
            icon: <BookOpen size={14} />,
            label: t('layout.backToHome'),
            onClick: () => navigate('/'),
          },
        ]
      : []),
    ...(isRoot
      ? [
          {
            key: 'setting',
            icon: <IconSettings />,
            label: t('nav.setting'),
            onClick: () => navigate('/console/setting'),
          },
        ]
      : []),
    {
      key: 'logout',
      icon: <IconPoweroff />,
      label: t('layout.logout'),
      onClick: onLogout,
    },
  ]

  return (
    <header className="console-topbar h-14 px-4 md:px-6 flex items-center justify-between flex-shrink-0 z-10">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <ConsoleIconButton
          aria-label={t('layout.openNavigation')}
          className="md:hidden flex-shrink-0"
          onClick={onOpenMobileNav}
        >
          <MenuIcon size={16} />
        </ConsoleIconButton>
        <ConsoleBreadcrumb mobileTitleOnly />
        <ConsoleBreadcrumb />
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <LanguageSwitcher variant="console" />

        <div className="hidden sm:block">
          <Badge count={0} dot>
            <ConsoleIconButton aria-label={t('layout.notifications')}>
              <Bell size={15} />
            </ConsoleIconButton>
          </Badge>
        </div>

        <ConsoleIconButton
          aria-label={t('layout.backToHome')}
          hideOnMobile
          onClick={() => navigate('/')}
        >
          <BookOpen size={15} />
        </ConsoleIconButton>

        <Dropdown
          droplist={
            <Menu
              onClickMenuItem={(key) => userDropdownItems.find((item) => item.key === key)?.onClick()}
            >
              <div className="console-user-menu-header px-3 py-2 border-b border-[var(--console-divider)]">
                <div className="text-[13px] font-semibold text-[var(--console-text)] truncate">
                  {displayName}
                </div>
                {user?.email ? (
                  <div className="text-[12px] text-[var(--console-text-muted)] truncate mt-0.5">
                    {user.email}
                  </div>
                ) : null}
              </div>
              {userDropdownItems.map((item) => (
                <Menu.Item key={item.key}>
                  <span className="inline-flex items-center gap-2">
                    {item.icon}
                    {item.label}
                  </span>
                </Menu.Item>
              ))}
            </Menu>
          }
          position="br"
        >
          <button
            type="button"
            className="console-avatar-btn ml-0.5"
            aria-label={displayName}
          >
            <Avatar
              size={32}
              style={{
                background: 'var(--brand-gradient)',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {getUserInitial(user)}
            </Avatar>
          </button>
        </Dropdown>
      </div>
    </header>
  )
}
